import classNames from 'classnames'
import IMask from 'imask'
import type { AnyMaskedOptions, InputMask, Masked } from 'imask'
import type { ChangeEvent, FC, KeyboardEvent } from 'react'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { computeWidth } from './compute-width'
import type {
  ExtendedAutocompleteInputProps,
  InputComponents,
  InputProps
} from './types'
import useExtractProps from './use-extract-props'
import useControllableState from '../../../hooks/use-controllable'
import type { PrivateThemeUiProps } from '../../../styles-system/create-component'
import createComponent from '../../../styles-system/create-component'
import type { Theme } from '../../../theme-provider/types'
import { useSafeThemeUI } from '../../../theme-provider/use-theme-ui'
import { isNotEmptyObject } from '../../../utils/assertion'
import { EMPTY_OBJECT, omit, pick } from '../../../utils/object'
import { getSpace, omitSpace } from '../../../utils/props-utils'
import { mergeRefs } from '../../../utils/react-helpers'
import sizeSynchronize from '../../../utils/size-synchronize'
import { formikField } from '../../_adaptors'
import { BoxPrivate } from '../box/box'
import type { BoxProps } from '../box/types'
import Flex from '../flex/flex'
import type { FlexProps } from '../flex/types'
import Icon from '../icon'
import LoadingSpinner from '../progress/loading-spinner'
import Text from '../text/text'
// import { useWhatChanged } from '@simbathesailor/use-what-changed'
// import useWhyDidYouUpdate from '../../../hooks/use-why-did-you-update'

const Component = createComponent('input', 'csg-input')

const _InputBase = React.forwardRef<
  HTMLElement,
  InputProps &
    PrivateThemeUiProps & {
      inline?: boolean
    }
>(function InputBase(props, ref) {
  const {
    title,
    inline,
    __css,
    maskOptions,
    selectedValue,
    value: valueProp,
    onBlur,
    onChange,
    onKeyDown,
    syncUnmaskedValueToValue,
    placeholder,
    ...rest
  } = props
  // useWhyDidYouUpdate('InputBase', props)
  const maskRef = useRef<undefined | InputMask<IMask.AnyMaskedOptions>>()

  const localRef = useRef<HTMLInputElement>()
  const maskPrepareFoundADecimal = useRef(false)

  const handleChange = useCallback(
    (maskEvt: React.FormEvent<HTMLInputElement>) => {
      if (maskEvt && onChange) {
        onChange({
          ...maskEvt,
          unmaskedValue: maskRef?.current?.unmaskedValue,
          masked: maskRef?.current?.masked
        } as unknown as React.FormEvent<HTMLInputElement>)
      }
    },
    [onChange]
  )

  const maskOptionsWithGracefulDecimalHandler = useMemo(() => {
    if (maskOptions) {
      const allowsDecimals = (maskOptions as { scale?: number })?.scale || false
      if (!allowsDecimals) {
        return Object.assign({}, maskOptions, {
          prepare: (value: string, masked: Masked<AnyMaskedOptions>) => {
            if (
              value === (masked as { radix?: string }).radix ||
              maskPrepareFoundADecimal.current === true
            ) {
              maskPrepareFoundADecimal.current = true
              return undefined
            }
            return value
          }
        })
      }
      return maskOptions
    }

    return undefined
  }, [maskOptions])

  useEffect(() => {
    if (!maskRef.current) {
      if (localRef?.current && maskOptionsWithGracefulDecimalHandler) {
        const m = IMask(localRef.current, maskOptionsWithGracefulDecimalHandler)
        maskRef.current = m
        maskRef.current.on('accept', handleChange)
      }
    } else {
      if (maskOptionsWithGracefulDecimalHandler) {
        maskRef.current.updateOptions(maskOptionsWithGracefulDecimalHandler)
        maskRef.current.on('accept', handleChange)
      }
    }

    return () => {
      //remove "event listener"
      maskOptionsWithGracefulDecimalHandler &&
        maskRef?.current?.off('accept', handleChange)
    }
  }, [maskOptionsWithGracefulDecimalHandler, handleChange])

  useEffect(() => {
    if (maskRef.current) {
      if (selectedValue === undefined) {
        maskRef.current.masked.resolve('')
        maskRef.current.el.value = ''
      } else {
        // maskRef.current.masked.resolve(initValue.toString())
        maskRef.current.typedValue =
          selectedValue instanceof Date
            ? selectedValue
            : selectedValue.toString()
      }
    }
  }, [selectedValue])

  // must be undefined if using a mask, otherwise conflict of who has control
  let value = undefined
  if (syncUnmaskedValueToValue && maskOptions && maskRef && maskRef.current) {
    maskRef.current.unmaskedValue = `${valueProp}`
  } else {
    value = valueProp
  }

  // eslint-disable-next-line no-console
  // console.log(
  //   `---- input render selectedValue:  ${selectedValue} value: ${valueProp} ---`
  // )

  const onBlurHandler = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (e: any) => {
      maskPrepareFoundADecimal.current = false

      if (!maskOptions) {
        return onBlur?.(e)
      }

      return onBlur?.({ ...e, unmaskedValue: maskRef?.current?.unmaskedValue })
    },
    [onBlur, maskOptions]
  )

  // Define state and function to update the value
  const [showTitle, setShowTitle] = useState(false)

  const handleOnMouseOver = useCallback(() => {
    const inputElement = localRef.current
    if (inputElement) {
      const isValueEmpty = !inputElement.value
      if (isValueEmpty && placeholder) {
        inputElement.value = placeholder
      }
      setShowTitle(inputElement.scrollWidth > inputElement.clientWidth)
      if (isValueEmpty) {
        inputElement.value = ''
      }
    }
  }, [localRef, placeholder])
  // eslint-disable-next-line no-console
  // console.log(` ---- render input [${localRef?.current?.name}] ----\r `)

  const onKeyDownWithDecimalSkipReset = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      maskPrepareFoundADecimal.current = false
      onKeyDown?.(e)
    },
    [onKeyDown]
  )

  const styles = useMemo(
    () => ({
      ...(inline ? { display: 'inline-block' } : {}),
      ...__css
    }),
    [__css, inline]
  )

  const _title = showTitle ? title || `${value || placeholder}` : ''

  // eslint-disable-next-line no-console
  // console.log(`---- input render value: ${value} title: ${_title}  ---`)

  return (
    <Component
      ref={mergeRefs(ref, localRef)}
      {...rest}
      onBlur={onBlurHandler}
      onChange={!maskOptions ? onChange : undefined}
      onKeyDown={onKeyDownWithDecimalSkipReset}
      onMouseOver={handleOnMouseOver}
      placeholder={placeholder}
      value={value}
      title={_title}
      __themeKey="forms"
      __css={styles}
    />
  )
})

export const InputBase = React.memo(_InputBase)

const defaultIconWrapper: FlexProps = {
  position: 'absolute',
  justifyContent: 'center',
  alignItems: 'center',
  top: 0,
  bottom: 0,
  gap: 'xxs'
}

const InputPrivate: FC<InputProps> = React.forwardRef<
  HTMLInputElement,
  InputProps &
    ExtendedAutocompleteInputProps & {
      theme?: Theme
    }
>(function InputPrivate(props, _ref) {
  // useWhyDidYouUpdate('InputPrivate', props)
  const theme = useSafeThemeUI(props)
  const {
    a11yId,
    a11yLabel,
    after,
    afterProps,
    autoComplete,
    before,
    beforeProps,
    className,
    Component,
    defaultValue,
    disableBorderEffect,
    disabled,
    height,
    hidableTags,
    inline,
    isLoading,
    isRequired,
    maskOptions,
    message,
    messageTextProps,
    mode,
    onChange,
    placeholder,
    readOnly,
    rootProps,
    showFocusStyle,
    size,
    state,
    sx,
    tagsVariant,
    type,
    valueProp,
    variant,
    name,
    color,
    autoWidthPadding,
    rest
  } = useExtractProps(props)

  const localRef = useRef<HTMLInputElement>()
  const ref = mergeRefs(_ref, localRef)
  const [autoWidthProps, setAutoWidthProps] = useState(EMPTY_OBJECT)

  const { fontSize, spinnerSize } = useMemo(() => {
    // this routine should be deprecated as we move away from using internal icon from theme-ui
    // to using icon from @costar/theme-ui-icon library. it's no longer necessary and size of icon
    // should be scale using combination of scale css and em unit depending on the icon.
    const { fontSize, spinnerSize } = sizeSynchronize(
      size ?? 'sm', // default to standard 1rem if not provide for default calculation
      theme,
      -1,
      -1
    )
    return {
      // don't include fontSize if size we not explicitly provided, this allow font size inheritances base on context
      ...(size ? { fontSize } : {}),
      spinnerSize
    }
  }, [size, theme])

  const {
    autoWidth,
    widthProps
  }: {
    autoWidth?: boolean
    widthProps: { width?: BoxProps['width'] }
  } = useMemo(() => {
    const retVal = {
      width: '100%',
      ...pick(props, ['width']),
      ...pick(sx, ['width'])
    } as { width: BoxProps['width'] }

    return retVal.width === 'auto'
      ? {
          autoWidth: true,
          widthProps: {}
        }
      : {
          widthProps: retVal
        }
  }, [props, sx])

  const [value, setValue] = useControllableState({
    value: valueProp,
    defaultValue
  }).state

  const { className: rootClassName, maxWidth, ...rootRest } = rootProps

  // useWhatChanged(
  //   [autoWidth, value, placeholder, maxWidth],
  //   'autoWidth, value, placeholder, maxWidth'
  // )

  useEffect(() => {
    if (autoWidth) {
      setAutoWidthProps({
        maxWidth,
        ...computeWidth(
          localRef.current,
          `${value || placeholder}`,
          autoWidthPadding
        )
      })
    }
  }, [autoWidth, value, placeholder, maxWidth, autoWidthPadding])

  const handelOnChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setValue(event.target.value)
      onChange && onChange(event)
    },
    [setValue, onChange]
  )

  const componentProps = useMemo(
    () => ({
      __inputProps: {
        type,
        minHeight: 'calc(2em - 2px)',
        ...(Component === BoxPrivate || autoWidth
          ? { width: '100%' }
          : {
              width: '0px',
              flexGrow: 1
            }),
        borderRadius: 'default',
        // explicit top and bottom to allow override below
        emPaddingTop: 'xs-3',
        emPaddingBottom: 'xs-3',
        px: 'xs',
        ...(after || isLoading
          ? {
              pr: 'xxl'
            }
          : {}),
        ...(before && Component === BoxPrivate
          ? {
              pl: 'xxl'
            }
          : {}),
        ...{
          ...getSpace(rest),
          ...pick(rest, ['lineHeight', 'minHeight'])
        },
        ...(color ? { color } : {})
      },
      sx: omit(sx, ['width']),
      __css: {
        position: 'relative',
        bg: 'white',
        ...(Component === BoxPrivate
          ? { height }
          : {
              minHeight: height
            })
      }
    }),
    [
      type,
      Component,
      autoWidth,
      after,
      isLoading,
      before,
      rest,
      sx,
      height,
      color
    ]
  )

  // eslint-disable-next-line no-console
  // console.log(`----- render input [${props?.name}] -----`)
  return (
    <Component
      className={classNames('csg-tui-input-wrapper', rootClassName, {
        'is-disabled': disabled,
        'is-read-only': mode !== 'select' && readOnly,
        'has-error': state === 'danger'
      })}
      __themeKey="forms"
      variant={variant ? `input.${variant}` : 'input'}
      disableBorderEffect={disableBorderEffect}
      fontSize={fontSize}
      disabled={disabled}
      readOnly={readOnly}
      state={state}
      inline={inline}
      hidableTags={hidableTags}
      tagsVariant={tagsVariant}
      mode={mode}
      maxWidth={autoWidth ? undefined : (maxWidth as BoxProps['maxWidth'])}
      {...{
        ...componentProps,
        ...(before && Component !== BoxPrivate
          ? {
              flexWrap: 'wrap',
              pl: 'xs-2',
              pr: 'lg',
              minWidth: '0px',
              flexGrow: 1,
              alignItems: 'baseline'
            }
          : {}),
        ...(isNotEmptyObject(autoWidthProps) ? { style: autoWidthProps } : {}),
        ...rootRest,
        ...widthProps
      }}
    >
      {before && Component === BoxPrivate ? (
        <Flex
          {...{
            ...defaultIconWrapper,
            left: '0.5em',
            ...beforeProps
          }}
        >
          {typeof before === 'function' ? before() : before}
        </Flex>
      ) : typeof before === 'function' ? (
        before()
      ) : (
        before
      )}
      {after && !isLoading && (
        <Flex
          {...{
            ...defaultIconWrapper,
            right: '0.5em',
            ...afterProps
          }}
        >
          {typeof after === 'function' ? after() : after}
        </Flex>
      )}
      <InputBase
        ref={ref}
        id={a11yId}
        type={type}
        className={classNames(`csg-tui-${type}-input`, className, {
          'show-focus': showFocusStyle
        })}
        aria-invalid={state === 'danger'}
        aria-label={a11yLabel}
        aria-required={isRequired}
        disabled={disabled}
        readOnly={readOnly}
        state={state}
        maskOptions={maskOptions}
        value={value}
        placeholder={placeholder}
        onChange={maskOptions ? onChange : handelOnChange}
        disableBorderEffect={disableBorderEffect}
        autoComplete={autoComplete}
        name={name}
        {...omitSpace(
          omit(rest, ['width', 'lineHeight', 'fontSize', 'minHeight'])
        )}
      />
      {isLoading && (
        <LoadingSpinner
          display="flex"
          size={spinnerSize}
          position="absolute"
          top="0"
          bottom="0"
          zIndex="2"
          right="sm"
          height="100%"
          paddingY="xxs"
        />
      )}
      {message && (
        <Text
          absolute
          className={`csg-tui-${type}-input-msg`}
          fontSize="xxs"
          lineHeight="body"
          mt="xs-2"
          gap="xs-2"
          {...messageTextProps}
          {...(state === 'danger'
            ? {
                display: 'flex',
                alignItems: 'center'
              }
            : undefined)}
        >
          {state === 'danger' ? (
            <Icon icon="info" color="danger" size="1em" />
          ) : null}
          {message}
        </Text>
      )}
    </Component>
  )
})

export const MemoInput = React.memo(InputPrivate)

//@ts-ignore
const Input: FC<InputProps> & InputComponents = React.forwardRef<
  HTMLInputElement,
  InputProps
>(function Input(props, ref) {
  return <MemoInput ref={ref} {...props} />
})

Input.Formik = formikField(Input, {
  hasFieldWrapper: true
})

export default Input
